import React, { useEffect, useState, useMemo } from "react";
import styles from "./OrderDriverBox.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { orderActions } from "../../../store/order-slice";
import { RootState } from "../../../store";
import moment from "moment";
import Button from "../../UI/Buttons/Button/Button";
import ChatBubble from "../../UI/ChatBubble/ChatBubble";
import classNames from "classnames";
import {
  fetchPermitsForDriver,
  finishOrderForDriverAction,
  pickupOrderWithVerificationAction,
  processOrderForDriverAction,
  uploadDeliveryConsent,
} from "../../../store/order-actions";
import { fetchDriverSignature } from "../../../store/auth-actions";
import SignatureUploadModal from "../../SignatureUploadModal/SignatureUploadModal";
import ReactModalWrap from "../../UI/Modals/ReactModalWrap/ReactModalWrap";
import MapWrap from "../../MapWrap/MapWrap";
import ArrowUpIcon from "@rsuite/icons/ArrowUp";
import PhoneIcon from "@rsuite/icons/Phone";
import DriverQRButton from "../DriverQRButton/DriverQRButton";
import { ReactComponent as RocketIcon } from "../../../assets/icons/rocket-icon.svg";
import { useRouteData } from "../../../hooks/useRouteData";
import type { RouteData } from "../../../types/routes";
import { useLocation } from "../../../contexts/LocationContext";
import OpeningHoursModal, {
  hasOpeningHoursData,
} from "./OpeningHoursModal/OpeningHoursModal";

import { CnCOrderResponse, DeliveryPaymentType } from "../../../types/api";
import {
  UserAddressResponse,
  MarketplaceResponse,
  TimeSlotResponse as DeliveryTimeSlot,
  OrderItem,
  AppBrandResponse,
} from "../../../types/shared";

interface DeliveryDisplayItem {
  label: string;
  value: string;
  isLoyalty: boolean;
  key: string;
  title: string;
}

interface DeliveryTimeSlotDisplayProps {
  deliveryTimeSlot?: DeliveryTimeSlot | null;
}

interface DeliveryDetailsProps {
  orderBoxData: {
    phoneNumber: string;
    userAddressResponse?: UserAddressResponse; // Keep optional since it can be missing
    noteForDelivery?: string;
    marketplaceResponse?: MarketplaceResponse; // Keep optional since it can be missing
    deliveryTimeSlot?: DeliveryTimeSlot | null;
    noteForUser?: string;
    serviceItems: OrderItem[]; // Required - guaranteed to be array now
    deliveryPaymentType?: DeliveryPaymentType;
    deliveryType?: string;
    deliveryPrice?: number;
  };
  archived?: boolean;
  isAdmin?: boolean;
  routeData?: RouteData;
  routeLoading?: boolean; // Route loading state
  routeError?: string | null; // Route error state
  userLocation?: [number, number] | null;
  pharmacyLocation?: [number, number] | null;
  customerLocation?: [number, number] | null;
}

type Props = {
  order: CnCOrderResponse | any; // Required - matches cnc-front pattern
  archived?: boolean;
  processed?: boolean;
  isAdmin?: boolean;
};

// Address Display Component Props
interface AddressDisplayProps {
  orderBoxData: {
    marketplaceResponse?: MarketplaceResponse;
    userAddressResponse?: UserAddressResponse;
  };
  isAdmin?: boolean;
  routeData?: RouteData;
  routeLoading?: boolean;
  routeError?: string | null;
  archived?: boolean;
}

// Reusable AddressDisplay Component with original routeInfoWrap styling
const AddressDisplay: React.FC<AddressDisplayProps> = ({
  isAdmin = false,
  orderBoxData,
  routeData,
  routeLoading = false,
  routeError = null,
  archived = false,
}) => {
  // Hook must be declared before any conditional return (Rules of Hooks).
  const [openingHoursModalOpen, setOpeningHoursModalOpen] = useState(false);

  // Skip for archived orders
  if (archived) return null;

  const hasRequiredData =
    orderBoxData.marketplaceResponse && orderBoxData.userAddressResponse;
  if (!hasRequiredData) return null;

  // SINGLE source of truth for address formatting
  const safeMarketplace = orderBoxData.marketplaceResponse!;
  const safeUserAddress = orderBoxData.userAddressResponse!;

  // Pharmacy address formatting
  const pharmacyName = safeMarketplace.name || "Apoteka";
  const pharmacyAddress = safeMarketplace.address || "Adresa nedostupna";
  const pharmacyLocation = safeMarketplace.location || "Lokacija nedostupna";
  const fullPharmacyAddress = [pharmacyName, pharmacyAddress, pharmacyLocation]
    .filter(Boolean)
    .join(", ");

  // Only offer the opening-hours trigger when the row actually carries hours.
  // Transaction-sourced rows have openingHoursResponse === null, and an
  // all-"/" modal would misrepresent the pharmacy as having no hours.
  const showOpeningHours = hasOpeningHoursData(
    safeMarketplace.openingHoursResponse,
  );

  // Customer address formatting with proper comma separation
  const customerName = safeUserAddress.name || "";
  const customerNumber = safeUserAddress.number || "";
  const customerCity = safeUserAddress.city || "";
  const fullCustomerAddress = [customerName, customerNumber, customerCity]
    .filter(Boolean)
    .join(", ");

  // Apartment info
  const floor = safeUserAddress.floor;
  const apartment = safeUserAddress.apartment;

  // Simple apartment info renderer
  const renderApartmentInfo = () => {
    if (!floor && !apartment) return null;

    return (
      <div className={styles.apartmentInfo}>
        {floor && (
          <>
            <strong>Sprat:</strong> {floor}
          </>
        )}
        {floor && apartment && ", "}
        {apartment && (
          <>
            <strong>Stan:</strong> {apartment}
          </>
        )}
      </div>
    );
  };

  // Route distance info
  const renderDistanceInfo = () => {
    if (routeLoading) {
      return <div>Računanje rute...</div>;
    }

    if (routeError) {
      return <div>Ruta nedostupna</div>;
    }

    const { pharmacyToCustomer } = routeData || {};
    if (pharmacyToCustomer?.distance || pharmacyToCustomer?.duration) {
      return (
        <div className={styles.routeDistance}>
          <div className={styles.distanceValue}>
            <strong>Distanca: </strong>
            {pharmacyToCustomer.distance}
            {pharmacyToCustomer.duration && ` • ${pharmacyToCustomer.duration}`}
          </div>
          <div className={styles.distanceNote}>
            (od apoteke do adrese za isporuku)
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={styles.routeInfoWrap}>
      <div className={styles.routeAddresses}>
        <div className={styles.routeAddress}>
          <span className={styles.addressLabel}>Adresa apoteke:</span>
          <span className={styles.addressValue}>{fullPharmacyAddress}</span>
        </div>

        {showOpeningHours && (
          <button
            type="button"
            className={styles.openingHoursLink}
            onClick={(event) => {
              event.stopPropagation();
              setOpeningHoursModalOpen(true);
            }}
          >
            Radno vreme apoteke
          </button>
        )}

        <div className={styles.routeAddress}>
          <span className={styles.addressLabel}>Adresa za isporuku:</span>
          <span className={styles.addressValue}>{fullCustomerAddress}</span>
        </div>

        {renderApartmentInfo()}

        {!isAdmin && renderDistanceInfo()}
      </div>

      {showOpeningHours && (
        <OpeningHoursModal
          isOpen={openingHoursModalOpen}
          onClose={() => setOpeningHoursModalOpen(false)}
          openingHoursResponse={safeMarketplace.openingHoursResponse}
          pharmacyName={safeMarketplace.name}
        />
      )}
    </div>
  );
};

// App Brand Display Component
interface AppBrandDisplayProps {
  appBrand?: AppBrandResponse | null;
  className?: string;
}

const AppBrandDisplay: React.FC<AppBrandDisplayProps> = ({
  appBrand,
  className = "",
}) => {
  const [imageError, setImageError] = useState(false);

  // Return null if no app brand data or neither name nor logo exists
  if (!appBrand || (!appBrand.name && !appBrand.logo)) {
    return null;
  }

  // Construct CDN URL if logo exists
  const logoUrl = appBrand.logo
    ? `${process.env.REACT_APP_CDN_PATH}${appBrand.logo}`
    : null;

  return (
    <div className={`${styles.appBrand} ${className}`}>
      {logoUrl && !imageError && (
        <img
          src={logoUrl}
          alt={appBrand.name ? `${appBrand.name} logo` : "App brand logo"}
          className={styles.appBrandLogoImage}
          onError={() => setImageError(true)}
          onLoad={() => setImageError(false)}
          loading="eager"
        />
      )}
      {appBrand.name && (
        <span className={styles.appBrandValue}>{appBrand.name}</span>
      )}
    </div>
  );
};

const OrderDriverBox = ({ order, processed, archived, isAdmin }: Props) => {
  // Move hooks to the very top, before any logic
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const [timeSelected] = useState<number | null>(null);

  // Use shared location instead of individual geolocation
  const { userLocation } = useLocation();

  // Simple data transformation - order is guaranteed to exist
  const orderBoxData = {
    ...order,
    id: order.id,
    date: new Date(order.created),
    firstName: order.firstName,
    secondName: order.secondName,
    phoneNumber:
      order.userPhoneNumberForOrder ||
      order.userPhoneNumber ||
      order.userResponse?.phoneNumber ||
      "",
    items: order.items,
    serviceItems: order.serviceItems || [], // Service items support
    noteForUser: order.noteForUser,
    rfzoTherapyNumber: order.rfzoTherapyNumber,
    status: order.status,
    totalPrice: order.totalPrice,
    totalItemsPrice: order.totalItemsPrice,
    deliveryPrice: order.deliveryPrice,
    deliveryPaymentType: order.deliveryPaymentType, // Delivery payment type
    deliveryType: order.deliveryType,
    totalPriceWithDelivery: order.totalPriceWithDelivery,
    userAddressResponse: order.userAddressResponse,
    marketplaceResponse: order.marketplaceResponse,
  };

  // Extract location data for route calculations
  const customerLat = orderBoxData.userAddressResponse?.latitude;
  const customerLng = orderBoxData.userAddressResponse?.longitude;
  const pharmacyLat = orderBoxData.marketplaceResponse?.latitude;
  const pharmacyLng = orderBoxData.marketplaceResponse?.longitude;

  // Remove this entire block - no more individual geolocation:
  // useEffect(() => {
  //   navigator.geolocation.getCurrentPosition(
  //     (position) => {
  //       const { latitude, longitude } = position.coords;
  //       setUserLocation([latitude, longitude]);
  //     },
  //     (error) => {
  //       console.warn("Geolocation failed:", error);
  //     }
  //   );
  // }, []);

  // Create stable array references to prevent infinite re-renders
  const pharmacyLocation = useMemo(
    () =>
      pharmacyLat != null && pharmacyLng != null
        ? ([pharmacyLat, pharmacyLng] as [number, number])
        : null,
    [pharmacyLat, pharmacyLng],
  );

  const customerLocation = useMemo(
    () =>
      customerLat != null && customerLng != null
        ? ([customerLat, customerLng] as [number, number])
        : null,
    [customerLat, customerLng],
  );

  // Determine when to fetch expensive polyline data (only when map will be displayed)
  const shouldSkipRouteApi = !!archived || !!isAdmin;
  const hasValidLocations = !!(customerLocation && pharmacyLocation);
  const shouldFetchPolylines =
    expanded && !shouldSkipRouteApi && hasValidLocations;

  // Use custom hook for route data fetching with archived optimization
  const {
    routeData,
    loading: routeLoading,
    error: routeError,
  } = useRouteData({
    userLocation,
    pharmacyLocation,
    customerLocation,
    archived: shouldSkipRouteApi, // Pass archived status to skip API calls for completed orders
    includePolylines: shouldFetchPolylines, // Only fetch polylines when map will be displayed
  });

  const showModal = () => {
    dispatch(orderActions.toggleOrderModal(order));
    setExpanded(true);
  };

  // 1. Service items display - independent of payment type (future-ready)
  const getServiceItemsDisplay = (): DeliveryDisplayItem[] => {
    const serviceItems = orderBoxData.serviceItems || [];
    if (serviceItems.length > 0) {
      return serviceItems.map((item: OrderItem, index: number) => ({
        label: `${item.itemResponse.name.trim()}:`,
        value: `${item.price.toFixed(2)} RSD`,
        isLoyalty: false,
        key: `service-${index}`,
        title: "Uključeno u ukupnu cenu porudžbine",
      }));
    }
    return [];
  };

  // 2. Delivery fee display - only for non-loyalty, non-service cases
  const getDeliveryFeeDisplay = (): DeliveryDisplayItem[] => {
    const isLoyaltyPoints =
      orderBoxData.deliveryPaymentType === "LOYALTY_POINTS";
    const hasServiceItems = (orderBoxData.serviceItems || []).length > 0;

    // Only show delivery fee if it's not loyalty points and no service items
    if (
      !isLoyaltyPoints &&
      !hasServiceItems &&
      orderBoxData.deliveryPrice &&
      orderBoxData.deliveryPrice > 0
    ) {
      return [
        {
          label: "Dostava:",
          value: `${orderBoxData.deliveryPrice.toFixed(2)} RSD`,
          isLoyalty: false,
          key: "delivery-fee",
          title: "Cena dostave",
        },
      ];
    }
    return [];
  };

  // 3. Loyalty points info - separate from delivery display
  const getLoyaltyInfo = (): {
    earnings: string;
  } | null => {
    const isLoyaltyPoints =
      orderBoxData.deliveryPaymentType === "LOYALTY_POINTS";
    if (!isLoyaltyPoints) return null;

    const points = orderBoxData.deliveryPrice || 250;
    return {
      earnings: `+${points}`,
    };
  };

  // 4. Loyalty section render - separate function for consistency
  const renderLoyaltySection = () => {
    const loyaltyInfo = getLoyaltyInfo();
    if (!loyaltyInfo) return null;

    return (
      <div className={styles.loyaltySection}>
        <div className={styles.loyaltyEarnings}>
          <span className={styles.loyaltyLabel}>Volonter:</span>
          <span className={styles.loyaltyPoints}>
            {loyaltyInfo.earnings}
            <img src="/loyalty-icon.png" alt="Loyalty Points Icon" />
          </span>
        </div>
      </div>
    );
  };

  return (
    <div
      className={classNames(styles.orderBox, {
        [styles.archived]: archived && order.status === "TAKEN",
        [styles.statusPrepared]:
          !archived &&
          order.status === "PREPARED" &&
          order.userDeliveryResponse != null,
        [styles.statusInDelivery]:
          !archived &&
          order.status === "IN_DELIVERY" &&
          order.userDeliveryResponse != null,
      })}
    >
      <div className={styles.cardHeader}>
        <div className={styles.dataAndId}>
          <span className={styles.orderDate}>
            {moment(orderBoxData.date).format("DD.MM.YYYY")}
            <span className={styles.orderTime}>
              {" "}
              • {moment(orderBoxData.date).format("HH:mm")}
            </span>
          </span>
          <br />
          <span className={styles.orderId}>
            #{orderBoxData.id}
            {orderBoxData.deliveryType === "FAST_DELIVERY" && (
              <RocketIcon style={{ marginLeft: "8px", placeSelf: "center" }} />
            )}
          </span>
        </div>

        <div className={styles.appBrandSection}>
          <AppBrandDisplay appBrand={order.userResponse.appBrand} />
        </div>
      </div>

      <div className={styles.cardBody}>
        <div className={styles.userName}>
          {orderBoxData.firstName}
          <br />
          {orderBoxData.secondName}
          <br />
        </div>
        {archived && orderBoxData.marketplaceResponse?.name && (
          <div className={styles.pharmacyName}>
            Apoteka: {orderBoxData.marketplaceResponse.name}
          </div>
        )}
      </div>

      <AddressDisplay
        isAdmin={isAdmin}
        orderBoxData={orderBoxData}
        routeData={routeData}
        routeLoading={routeLoading}
        routeError={routeError}
        archived={archived}
      />

      {!archived && (
        <DeliveryTimeSlotDisplay
          deliveryTimeSlot={orderBoxData.deliveryTimeSlot}
        />
      )}

      {expanded && (
        <DeliveryDetails
          isAdmin={isAdmin}
          orderBoxData={orderBoxData}
          archived={archived}
          routeData={routeData}
          routeLoading={routeLoading} // Route loading state
          routeError={routeError} // Route error state
          userLocation={userLocation}
          pharmacyLocation={pharmacyLocation}
          customerLocation={customerLocation}
        />
      )}

      <div className={styles.pricesWrap}>
        {/* Order value */}
        {!(processed && !expanded) && (
          <div className={styles.priceItem}>
            <span className={styles.label}>Vrednost porudzbine:</span>
            <span className={styles.value}>
              {(
                orderBoxData.totalItemsPrice || orderBoxData.totalPrice
              ).toFixed(2)}{" "}
              RSD
            </span>
          </div>
        )}

        {/* Service items (independent of payment type) */}
        {getServiceItemsDisplay().map((item) => (
          <div key={item.key} className={styles.priceItem} title={item.title}>
            <span className={styles.label}>{item.label}</span>
            <span className={styles.value}>{item.value}</span>
          </div>
        ))}

        {/* Delivery fee (only for non-loyalty, non-service cases) */}
        {getDeliveryFeeDisplay().map((item) => (
          <div key={item.key} className={styles.priceItem} title={item.title}>
            <span className={styles.label}>{item.label}</span>
            <span className={styles.value}>{item.value}</span>
          </div>
        ))}

        {/* Total price */}
        <div className={styles.priceItem}>
          <span className={styles.label}>Ukupna cena:</span>
          <span className={styles.value}>
            <b>{orderBoxData.totalPrice.toFixed(2)} RSD</b>
          </span>
        </div>
      </div>

      {order.userDeliveryResponse && (
        <div className={styles.priceItem}>
          <span className={styles.label}>Volonter: </span>
          <span className={styles.value}>
            {order.userDeliveryResponse.firstName}{" "}
            {order.userDeliveryResponse.secondName}
          </span>
          {order.userDeliveryResponse.phoneNumber && (
            <div className={styles.value}>
              {`Telefon: ${order.userDeliveryResponse.phoneNumber}`}
            </div>
          )}
        </div>
      )}

      {/* Loyalty points section - separate from pricesWrap */}
      {renderLoyaltySection()}

      {expanded && !isAdmin && (
        <ProcessButtons order={orderBoxData} timeSelected={timeSelected} />
      )}

      {!expanded && (
        <div className={styles.cardFooter}>
          <Button
            style={{ fontSize: "14px" }}
            onClick={() => {
              showModal();
            }}
            buttonType={archived ? "blue" : "blue"}
            disabled={!archived && orderBoxData.status === "CREATED"}
            title={
              !archived && orderBoxData.status === "CREATED"
                ? "Nije dostupno dok apoteka ne prihvati rezervaciju"
                : ""
            }
          >
            {archived ? "Otvori" : "Pogledaj detalje"}
          </Button>
        </div>
      )}

      {expanded && (
        <div
          style={{ textAlign: "center", color: "#a9a9a9", fontSize: "20px" }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (expanded) {
              setExpanded(false);
              dispatch(orderActions.toggleOrderModal(null));
            }
          }}
        >
          <ArrowUpIcon height={42} width={42} />
        </div>
      )}
    </div>
  );
};

export default OrderDriverBox;

// Updated DeliveryDetails with service items and payment info
const DeliveryDetails: React.FC<DeliveryDetailsProps> = ({
  orderBoxData,
  archived,
  isAdmin,
  routeData,
  routeLoading = false, // Route loading state with default
  routeError = null, // Route error state with default
  userLocation,
  pharmacyLocation,
  customerLocation,
}) => {
  // Suppress unused variable warnings for parameters kept for future use
  void routeLoading;
  void routeError;
  // Safe destructuring with fallbacks
  const {
    phoneNumber = "",
    userAddressResponse,
    noteForDelivery,
    marketplaceResponse,
    serviceItems, // No default needed - guaranteed to be array
    deliveryPaymentType,
    deliveryType,
    deliveryPrice,
  } = orderBoxData || {};

  // Safe address data with proper typing
  const safeUserAddress =
    userAddressResponse || ({} as Partial<UserAddressResponse>);
  const safeMarketplace =
    marketplaceResponse || ({} as Partial<MarketplaceResponse>);

  const {
    name = "",
    city = "",
    number = "",
    floor = "",
    apartment = "",
  } = safeUserAddress;
  const { name: marketName = "", address: marketAddress = "" } =
    safeMarketplace;

  // Extract location data with proper validation
  const customerLat = safeUserAddress.latitude;
  const customerLng = safeUserAddress.longitude;
  const pharmacyLat = safeMarketplace.latitude;
  const pharmacyLng = safeMarketplace.longitude;

  // Enhanced conditional logic for map display - use props from parent
  const hasValidLocations = customerLocation && pharmacyLocation;
  const shouldShowMap = !archived && hasValidLocations;

  return (
    <div className={styles.deliveryDetailsWrap}>
      {phoneNumber && (
        <div className={styles.phoneNumberWrap}>
          <PhoneIcon />
          <a className={styles.phoneNumber} href={`tel:${phoneNumber}`}>
            {phoneNumber}
          </a>
        </div>
      )}

      {!isAdmin && shouldShowMap && (
        <div className={styles.mapContainer}>
          {(() => {
            try {
              return (
                <MapWrap
                  userLocation={userLocation || null}
                  customerLocation={customerLocation!}
                  pharmacyLocation={pharmacyLocation!}
                  routeData={routeData}
                />
              );
            } catch (error) {
              console.warn("Map rendering failed:", error);
              return (
                <div
                  style={{
                    padding: "20px",
                    textAlign: "center",
                    color: "#666",
                  }}
                >
                  Mapa trenutno nije dostupna
                </div>
              );
            }
          })()}
        </div>
      )}

      <div className={styles.deliveryNote}>
        <span
          className={classNames(styles.detailsLabel, styles.deliveryNoteLabel)}
        >
          Dodatne napomene:
        </span>
        <ChatBubble noteFromUserFlag>
          {noteForDelivery ? (
            noteForDelivery
          ) : (
            <span className={styles.NoNoteFromUser}>
              Korisnik nije uneo napomenu
            </span>
          )}
        </ChatBubble>
      </div>

      {/* Pharmacy notes section - address info now handled by AddressDisplay */}
      <div className={styles.pharmacyNotesSection}>
        <span className={styles.detailsLabel}>Napomena apoteke:</span>
        <ChatBubble noteFromUserFlag>
          <span>
            {orderBoxData.noteForUser || "Apoteka nije unela napomenu"}
          </span>
        </ChatBubble>
      </div>
    </div>
  );
};

// Helper components remain the same...
const DeliveryTimeSlotDisplay: React.FC<DeliveryTimeSlotDisplayProps> = ({
  deliveryTimeSlot,
}) => {
  const formatDeliveryTime = (): string => {
    if (!deliveryTimeSlot?.day || !deliveryTimeSlot?.hoursSlot) {
      return "/";
    }
    return `${deliveryTimeSlot.day}, ${deliveryTimeSlot.hoursSlot}`;
  };

  return (
    <div className={styles.deliveryTimeSlot}>
      <span>Željeno vreme isporuke:</span>
      <div className={styles.deliveryTimeValue}>{formatDeliveryTime()}</div>
    </div>
  );
};

// Verification Code Modal Component
interface VerificationCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (code: string) => void;
  verificationCode: string;
  setVerificationCode: (code: string) => void;
  verificationError: string;
  isSubmitting: boolean;
  order: any;
}

const VerificationCodeModal: React.FC<VerificationCodeModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  verificationCode,
  setVerificationCode,
  verificationError,
  isSubmitting,
  order,
}) => {
  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow 4 digits
    const value = e.target.value.replace(/\D/g, "").slice(0, 4);
    setVerificationCode(value);
  };

  const handleSubmit = () => {
    if (verificationCode.length === 4) {
      onSubmit(verificationCode);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && verificationCode.length === 4) {
      handleSubmit();
    }
  };

  return (
    <ReactModalWrap isOpen={isOpen} close={onClose}>
      <div className={styles.verificationModalContent}>
        <div className={styles.verificationModalHeader}>
          <h3>Unesite verifikacioni kod</h3>
          <p className={styles.orderNumber}>Porudžbina #{order.id}</p>
        </div>

        <div className={styles.verificationModalBody}>
          <label
            htmlFor="verification-code-input"
            className={styles.instructionText}
          >
            Unesite 4-cifreni verifikacioni kod:
          </label>

          <div className={styles.codeInputWrapper}>
            <input
              id="verification-code-input"
              name="verificationCode"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              value={verificationCode}
              onChange={handleCodeChange}
              onKeyDown={handleKeyDown}
              placeholder="0000"
              className={styles.codeInput}
              autoFocus
              disabled={isSubmitting}
              aria-label="Verifikacioni kod"
            />
          </div>

          {verificationError && (
            <div className={styles.verificationError}>{verificationError}</div>
          )}

          <div className={styles.buttonGroup}>
            <Button
              buttonType="blue"
              onClick={handleSubmit}
              disabled={verificationCode.length !== 4 || isSubmitting}
              style={{ marginBottom: "10px" }}
            >
              {isSubmitting ? "Proverava se..." : "Potvrdi"}
            </Button>
            <Button
              buttonType="white"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Otkaži
            </Button>
          </div>
        </div>
      </div>
    </ReactModalWrap>
  );
};

const ProcessButtons = ({ order, timeSelected }: any) => {
  const dispatch = useDispatch();
  const [unsignedPermits, setUnsignedPermits] = useState<any>([]);
  const userIdFromLs = localStorage.getItem("id");
  const userId = userIdFromLs ? JSON.parse(userIdFromLs) : null;

  // Signature state for order acceptance gate
  const { driverSignature, loadingDriverSignature } = useSelector(
    (state: RootState) => state.auth,
  );
  const [showSignatureModal, setShowSignatureModal] = useState(false);

  // ADD VERIFICATION STATE
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationError, setVerificationError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Loading states for action buttons
  const [isAccepting, setIsAccepting] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);

  // Gate: Check if driver has signature before accepting order
  const handleAcceptOrder = async () => {
    if (isAccepting) return; // Prevent duplicate calls

    // Don't block if still loading - assume has signature
    if (!loadingDriverSignature && !driverSignature) {
      setShowSignatureModal(true);
      return;
    }

    setIsAccepting(true);
    try {
      await dispatch<any>(processOrderForDriverAction(order.id, userId));
    } finally {
      setIsAccepting(false);
    }
  };

  // Handle successful signature upload
  const handleSignatureSuccess = async () => {
    setShowSignatureModal(false);
    // Refresh signature in Redux
    if (userId) {
      dispatch<any>(fetchDriverSignature(userId));
    }

    // Proceed with order acceptance
    setIsAccepting(true);
    try {
      await dispatch<any>(processOrderForDriverAction(order.id, userId));
    } finally {
      setIsAccepting(false);
    }
  };

  // NEW: Handle pharmacy pickup with verification
  const handlePickupWithCode = () => {
    setShowVerificationModal(true);
    setVerificationCode("");
    setVerificationError("");
  };

  const handleVerificationSubmit = async (code: string) => {
    setIsSubmitting(true);
    setVerificationError("");

    try {
      // Call the NEW pickup action with verification code
      await dispatch<any>(
        pickupOrderWithVerificationAction(order.id, code, order.deliveryType),
      );

      // Success - close modal
      setShowVerificationModal(false);
      setVerificationCode("");
    } catch (error: any) {
      // Handle 403 error (incorrect code)
      if (error?.response?.status === 403) {
        setVerificationError(
          "Neispravan kod. Proverite kod sa apotekom i pokušajte ponovo.",
        );
      } else {
        setVerificationError("Greška prilikom provere koda. Pokušajte ponovo.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeVerificationModal = () => {
    if (!isSubmitting) {
      setShowVerificationModal(false);
      setVerificationCode("");
      setVerificationError("");
    }
  };

  // EXISTING: Complete delivery to customer (no verification)
  const finishOrderAsDriver = async () => {
    if (isFinishing || !order?.transactionId) return;

    setIsFinishing(true);
    try {
      await dispatch<any>(finishOrderForDriverAction(order.transactionId));
    } finally {
      setIsFinishing(false);
    }
  };

  const fetchOrderPermits = async () => {
    if (order?.id && order?.userDeliveryResponse?.id) {
      try {
        const response = await fetchPermitsForDriver(
          order.id,
          order.userDeliveryResponse.id,
        );
        // Filter out PLATFORM_PERMIT
        setUnsignedPermits(
          (response?.data || []).filter(
            (permit: any) => permit.deliveryPermitType !== "PLATFORM_PERMIT",
          ),
        );
      } catch (error) {
        console.error("Failed to fetch permits:", error);
        setUnsignedPermits([]);
      }
    }
  };
  useEffect(() => {
    if (order?.userDeliveryResponse) {
      fetchOrderPermits().catch(console.error);
    }
  }, [order?.id, order?.userDeliveryResponse?.id]);

  return (
    <>
      {/* Signature Upload Modal */}
      <SignatureUploadModal
        isOpen={showSignatureModal}
        userId={userId}
        onClose={() => setShowSignatureModal(false)}
        onSuccess={handleSignatureSuccess}
      />

      {order.userDeliveryResponse == null ? (
        // STAGE 1: Order not accepted yet
        <>
          <Button
            buttonType="blue"
            onClick={handleAcceptOrder}
            disabled={isAccepting}
          >
            {isAccepting ? "Prihvatanje..." : "Prihvati porudžbinu"}
          </Button>
        </>
      ) : (
        <>
          {/* Permits upload */}
          {!order?.userResponse?.deliveryPermit && (
            <PermitsUploadWrap
              reFetchPermits={fetchOrderPermits}
              unsignedPermits={unsignedPermits}
              order={order}
            />
          )}

          {/* QR Button for payment */}
          <DriverQRButton order={order} />

          {/* STAGE 2: Order accepted - check status for next action */}
          {order.status === "PREPARED" ? (
            // Show pickup button with verification
            <Button
              buttonType="blue"
              onClick={handlePickupWithCode}
              disabled={isSubmitting}
              style={{ marginBottom: "10px" }}
            >
              {isSubmitting ? "Obrada..." : "Označi kao preuzeto"}
            </Button>
          ) : order.status === "IN_DELIVERY" ? (
            // Show delivery completion button
            <Button
              buttonType="blue"
              onClick={finishOrderAsDriver}
              disabled={isFinishing}
            >
              {isFinishing ? "Završavanje..." : "Dostavljeno"}
            </Button>
          ) : null}
        </>
      )}

      {/* ADD VERIFICATION MODAL */}
      <VerificationCodeModal
        isOpen={showVerificationModal}
        onClose={closeVerificationModal}
        onSubmit={handleVerificationSubmit}
        verificationCode={verificationCode}
        setVerificationCode={setVerificationCode}
        verificationError={verificationError}
        isSubmitting={isSubmitting}
        order={order}
      />
    </>
  );
};

export const PermitsUploadWrap = ({
  unsignedPermits,
  order,
  reFetchPermits,
}: any) => {
  // console.log(unsignedPermits); // Removed console.log to clean up console output
  if (unsignedPermits?.length > 0) {
    return (
      <div className={styles.permitsUploadWrap}>
        {unsignedPermits?.map((permit: any, key: number) => (
          <PermitUpload
            key={key}
            reFetchPermits={reFetchPermits}
            permit={permit}
            order={order}
          />
        ))}
      </div>
    );
  }

  return null;
};

export const PermitUpload = ({ order, permit, reFetchPermits }: any) => {
  const [file, setFile] = useState<File | null>(null);

  const handleFileUpload = (e: any) => {
    const renamedFile = renameFile(e.target.files[0], `p${permit.id}`);
    setFile(renamedFile);
  };

  const submitUpload = () => {
    // console.log("fired");
    const formData = new FormData();
    if (file !== null) {
      formData.append("images", file);
    }
    uploadDeliveryConsent(formData, permit.id).then((res) => reFetchPermits());
    setFile(null);
  };

  let permitHref = "";
  let permitLabel = "";
  if (permit?.signed) {
    permitHref =
      process.env.REACT_APP_CDN_PATH +
      "user_delivery_permits/" +
      permit.signedPermitFileName;
    permitLabel = file
      ? file?.name
      : permit?.signedPermitFileName + " (kliknite za izmenu)";
  } else {
    permitLabel = "Izaberi " + permit?.deliveryPermitType;
    permitHref =
      process.env.REACT_APP_CDN_PATH +
      "delivery_permits/p" +
      permit.id +
      ".pdf";
  }

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  return (
    <>
      <ConfirmModal
        isOpen={confirmModalOpen}
        setIsOpen={setConfirmModalOpen}
        confirm={() => {
          setConfirmModalOpen(false);

          submitUpload();
        }}
      />
      <div className={styles.permitUploadWrap}>
        <a
          className={styles.permitLink}
          target="_blank"
          rel="noreferrer"
          href={permitHref}
        >
          Preuzmite {permit?.signed ? "potpisani" : "nepotpisan"} PDF
        </a>
        <div className={styles.permitInputWrap}>
          <label htmlFor="file" className={styles.fileLabel}>
            <span className={styles.fileLabelText}>{permitLabel}</span>
          </label>
          <input
            id="file"
            className={styles.permitInput}
            onChange={(e) => handleFileUpload(e)}
            type="file"
          />
        </div>
        {
          <Button
            className={styles.uploadButton}
            disabled={!file}
            onClick={() => {
              permit?.signed ? setConfirmModalOpen(true) : submitUpload();
            }}
            buttonType="blue"
          >
            Pošalji
          </Button>
        }
      </div>
    </>
  );
};

const renameFile = (file: File, newName: string) => {
  const fileExtension = file.name.split(".").pop();
  const newFileName = `${newName}.${fileExtension}`;
  return new File([file], newFileName, { type: file.type });
};

const ConfirmModal = ({ isOpen, setIsOpen, confirm }: any) => {
  return (
    <ReactModalWrap
      isOpen={isOpen}
      close={() => {
        setIsOpen(false);
      }}
    >
      <div className={styles.confirmModalContent}>
        <div className={styles.header}>
          Da li ste sigurni da zelite da dodate novi fajl
        </div>

        <Button onClick={() => confirm()} buttonType="blue">
          DA
        </Button>
        <Button onClick={() => setIsOpen(false)} buttonType="white">
          NE
        </Button>
      </div>
    </ReactModalWrap>
  );
};
